import { useState, useEffect, useCallback, useRef } from 'react'

import { CountryModel } from '@marketplace-web/country/country-data'
import {
  transformPostalCodeDto,
  getPostalCodeConfiguration,
} from '@marketplace-web/address/address-data'

import { InputDropdownOption } from './Dsa/components/InputDropdownController'

function useCityOptions({
  country,
  postalCode,
  isCitySelectorAbTestOn,
}: {
  country: CountryModel | undefined
  postalCode: string
  isCitySelectorAbTestOn: boolean
}) {
  const [cityOptions, setCityOptions] = useState<Array<InputDropdownOption>>([])
  const [fullPostalCode, setFullPostalCode] = useState<string>()
  const abortController = useRef<AbortController | null>(null)

  const isPostalCodeValid = useCallback(
    (code: string) => {
      if (!country?.postalCodeConstraints) return false

      const { format, minLength, maxLength } = country.postalCodeConstraints
      const regExp = new RegExp(format)

      return code.length >= minLength && code.length <= maxLength && regExp.test(code)
    },
    [country],
  )

  const resetState = useCallback(() => {
    setCityOptions([])
    setFullPostalCode('')
  }, [])

  const validatePostalCodeAndFetchCities = useCallback(
    async (postalCodeValue: string) => {
      abortController.current?.abort()
      abortController.current = new AbortController()

      if (!country || !postalCodeValue || !isPostalCodeValid(postalCodeValue)) {
        resetState()

        return false
      }

      const response = await getPostalCodeConfiguration({
        code: postalCodeValue,
        countryId: country.id,
        signal: abortController.current?.signal,
      })

      if ('errors' in response) {
        resetState()

        return false
      }

      const { cities, code } = transformPostalCodeDto(response.postal_code)

      setCityOptions(cities.map(city => ({ value: city, label: city })))
      setFullPostalCode(code)

      return !!cities.length
    },
    [country, isPostalCodeValid, resetState],
  )

  useEffect(() => {
    if (isCitySelectorAbTestOn) return
    if (!country || !postalCode) return

    if (!isPostalCodeValid(postalCode)) {
      setCityOptions([])

      return
    }

    async function fetchPostalCodeConfiguration() {
      if (!country) return

      const response = await getPostalCodeConfiguration({
        code: postalCode,
        countryId: country.id,
      })

      if ('errors' in response) {
        setCityOptions([])
        setFullPostalCode('')

        return
      }

      const { cities, code } = transformPostalCodeDto(response.postal_code)

      setCityOptions(cities.map(city => ({ value: city, label: city })))
      setFullPostalCode(code)
    }

    fetchPostalCodeConfiguration()
  }, [country, postalCode, isPostalCodeValid, isCitySelectorAbTestOn])

  return {
    cityOptions,
    fullPostalCode,
    validatePostalCodeAndFetchCities,
    resetCityOptions: resetState,
  }
}

export default useCityOptions
